/**
 * Server-side Rate Limiter for Indexer Proxy
 *
 * Implements sliding window rate limiting per IP address
 * to protect the indexer from abuse.
 */

import { RateLimitError } from './errors'

interface RateLimitConfig {
  /** Maximum requests allowed in the window */
  maxRequests: number
  /** Window size in milliseconds */
  windowMs: number
}

interface RateLimitEntry {
  timestamps: number[]
  blocked: boolean
  blockedUntil: number
}

const DEFAULT_CONFIG: RateLimitConfig = {
  maxRequests: 150, // 150 requests
  windowMs: 60000, // per minute
}

const EXPENSIVE_PATH_CONFIG: RateLimitConfig = {
  maxRequests: 50, // 50 requests
  windowMs: 60000, // per minute
}

/**
 * In-memory rate limiter (for Vercel Edge/Serverless)
 *
 * Note: In production with multiple instances, you'd want to use
 * a distributed store like Upstash Redis. This implementation
 * works for single-instance or when using Vercel's Edge Runtime.
 */
class InMemoryRateLimiter {
  private entries = new Map<string, RateLimitEntry>()
  private cleanupInterval: ReturnType<typeof setInterval> | null = null

  constructor() {
    // Clean up old entries every 5 minutes
    if (typeof setInterval !== 'undefined') {
      this.cleanupInterval = setInterval(() => this.cleanup(), 5 * 60 * 1000)
    }
  }

  /**
   * Check if a request should be allowed
   * @returns true if allowed, throws RateLimitError if blocked
   */
  check(key: string, config: RateLimitConfig = DEFAULT_CONFIG): boolean {
    const now = Date.now()
    let entry = this.entries.get(key)

    if (!entry) {
      entry = {
        timestamps: [],
        blocked: false,
        blockedUntil: 0,
      }
      this.entries.set(key, entry)
    }

    // Check if currently blocked
    if (entry.blocked && now < entry.blockedUntil) {
      const retryAfter = Math.ceil((entry.blockedUntil - now) / 1000)
      throw new RateLimitError(retryAfter, 0)
    }

    // Remove timestamps outside the window
    entry.timestamps = entry.timestamps.filter((ts) => now - ts < config.windowMs)

    // Check if over limit
    if (entry.timestamps.length >= config.maxRequests) {
      // Block for the remainder of the window plus a penalty
      entry.blocked = true
      entry.blockedUntil = now + 60000 // 1 minute block

      const retryAfter = 60
      throw new RateLimitError(retryAfter, 0)
    }

    // Record this request
    entry.timestamps.push(now)
    entry.blocked = false

    return true
  }

  /**
   * Get current rate limit status for a key
   */
  getStatus(
    key: string,
    config: RateLimitConfig = DEFAULT_CONFIG,
  ): {
    remaining: number
    resetIn: number
    blocked: boolean
  } {
    const now = Date.now()
    const entry = this.entries.get(key)

    if (!entry) {
      return {
        remaining: config.maxRequests,
        resetIn: config.windowMs,
        blocked: false,
      }
    }

    const validTimestamps = entry.timestamps.filter((ts) => now - ts < config.windowMs)
    const oldestTimestamp = validTimestamps[0] ?? now

    return {
      remaining: Math.max(0, config.maxRequests - validTimestamps.length),
      resetIn: oldestTimestamp + config.windowMs - now,
      blocked: entry.blocked && now < entry.blockedUntil,
    }
  }

  private cleanup(): void {
    const now = Date.now()
    const windowMs = 5 * 60 * 1000 // Keep entries for 5 minutes

    for (const [key, entry] of this.entries.entries()) {
      // Remove entries with no recent activity
      const recentTimestamps = entry.timestamps.filter((ts) => now - ts < windowMs)
      if (recentTimestamps.length === 0 && (!entry.blocked || now > entry.blockedUntil)) {
        this.entries.delete(key)
      } else {
        entry.timestamps = recentTimestamps
      }
    }
  }

  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval)
      this.cleanupInterval = null
    }
    this.entries.clear()
  }
}

// Global rate limiter instance
let rateLimiter: InMemoryRateLimiter | null = null

function getRateLimiter(): InMemoryRateLimiter {
  if (!rateLimiter) {
    rateLimiter = new InMemoryRateLimiter()
  }
  return rateLimiter
}

/**
 * Check rate limit for a request
 * @param ip - IP address of the requester
 * @param isExpensive - Whether this is an expensive path (stricter limits)
 * @param isInternal - Internal cron requests bypass rate limiting
 */
export function checkRateLimit(ip: string, isExpensive = false, isInternal = false): void {
  if (isInternal) return
  const limiter = getRateLimiter()
  const config = isExpensive ? EXPENSIVE_PATH_CONFIG : DEFAULT_CONFIG
  limiter.check(ip, config)
}

/**
 * Get rate limit status for monitoring
 */
export function getRateLimitStatus(
  ip: string,
  isExpensive = false,
): {
  remaining: number
  resetIn: number
  blocked: boolean
} {
  const limiter = getRateLimiter()
  const config = isExpensive ? EXPENSIVE_PATH_CONFIG : DEFAULT_CONFIG
  return limiter.getStatus(ip, config)
}

export { InMemoryRateLimiter, DEFAULT_CONFIG, EXPENSIVE_PATH_CONFIG }
export type { RateLimitConfig }
